import { useEffect } from "react";
import { ReactComponent as Logo } from "assets/images/logo.svg";
import { ReactComponent as TextLogo } from "assets/images/text-logo.svg";
import { useSelector } from "react-redux";
import { UserState } from "redux-context/user";
import { BusinessState } from "redux-context/business";
import { DomainState } from "redux-context/domain";
import { OnboardingStep } from "enums/OnboardingStep";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faChartLine,
  faHouse,
  faCirclePlay,
  faGear,
  faHeart,
  faUser,
  faCommentDots,
} from "@fortawesome/free-solid-svg-icons";
import { ChatState } from "redux-context/chat";
import { CollabsState } from "redux-context/collabs";
import "./SideBar.scss";

interface SidebarProps {
  selectedKey: string;
  setSelectedKey: (key: string) => void;
  memuItems: any[];
  collapsed?: boolean;
  setCollapsed?: (b: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = (props) => {
  const { selectedKey, setSelectedKey, collapsed } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { loggedUser, loading } = useSelector((state: UserState) => state.user);
  const { business } = useSelector((state: BusinessState) => state.business);
  const {
    metadata: { whiteLabel, logoUrl, smallLogoUrl },
  } = useSelector((state: DomainState) => state.domain);
  const { totalUnreadCount } = useSelector((state: ChatState) => state.chat);
  const { numbers } = useSelector((state: CollabsState) => state.collabs);

  const isAdmin = loggedUser?.roles?.includes("admin");

  const disabled =
    loading ||
    (!loggedUser?.defaultBusinessId && !isAdmin) ||
    business?.onboardingStep === OnboardingStep.NotStarted;

  // define route mapping for navigation
  const items = [
    { key: "home", label: "Home", icon: faHouse, route: "/" },
    { key: "collabs", label: "Collabs", icon: faHeart, route: "/collabs" },
    {
      key: "campaigns",
      label: "Campaigns",
      icon: faCirclePlay,
      highlighted: true,
      route: "/campaigns",
    },
    {
      key: "applications",
      label: "Applications",
      icon: faPaperPlane,
      badge: numbers.pending,
      route: "/applications",
    },
    { key: "creators", label: "Creators", icon: faUser, route: "/creators" },
    {
      key: "report",
      label: "Report",
      icon: faChartLine,
      route: `/report?businessId=${business?._id}`,
    },
    {
      key: "messenger",
      label: "Messenger",
      icon: faCommentDots,
      badge: totalUnreadCount,
      route: "/messenger",
    },
    { key: "settings", label: "Settings", icon: faGear, route: "/settings" },
  ];

  useEffect(() => {
    const found = items.find(
      (it) =>
        location.pathname === it.route ||
        location.pathname.startsWith(it.route + "/"),
    );
    if (found && found.key !== selectedKey) {
      setSelectedKey(found.key);
    }
    // to avoid flicker, we only want to run this when location.pathname changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleClick = (item: (typeof items)[number]) => {
    if (disabled) return;
    if (item.route) {
      navigate(item.route);
    }
  };

  const asideClass = `sidebar ${collapsed ? "collapsed" : ""} ${
    disabled ? "disabled" : ""
  }`;

  return (
    <aside className={asideClass} aria-hidden={disabled}>
      <div>
        <div className="brand-container">
          <div className="logo-row">
            {whiteLabel ? (
              <div className="brand">
                <img
                  src={smallLogoUrl || logoUrl}
                  alt="Brand"
                  className="brand-img"
                />
                <span className="powered">
                  powered by <TextLogo />
                </span>
              </div>
            ) : (
              <Logo />
            )}
          </div>
        </div>

        <nav aria-label="Main navigation">
          <ul className="nav-list">
            {items.map((item) => {
              const isSelected = selectedKey === item.key;
              const liClass = `nav-item ${isSelected ? "selected" : ""} ${
                disabled ? "disabled" : ""
              }`;

              return (
                <li
                  key={item.key}
                  onClick={() => handleClick(item)}
                  className={liClass}
                  aria-current={isSelected ? "page" : undefined}
                >
                  <div className="icon">
                    <FontAwesomeIcon icon={item.icon as any} />
                  </div>

                  <span className="label">{item.label}</span>

                  {typeof item.badge === "number" &&
                    !collapsed &&
                    item.badge > 0 && (
                      <span className="badge">
                        {item.badge > 999 ? "999+" : item.badge}
                      </span>
                    )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      <div className="footer"></div>
    </aside>
  );
};

export default Sidebar;
